import * as Sentry from "@sentry/vue";
import {shouldCaptureAxiosError} from "./SentryHttpError.js";
import {SENTRY_APPLICATION_KEY} from "./SentryApplicationKey.js";

const getAxiosResponseRejection = (value) => {
  if (!value || typeof value !== "object") return null;
  if (typeof value.status !== "number") return null;
  if (!value.config || typeof value.config !== "object") return null;

  return value;
};

export const normalizeAxiosResponseRejectionEvent = (event, response) => {
  const method = response?.config?.method?.toUpperCase?.() ?? "UNKNOWN";
  const url = response?.config?.url ?? "unknown-url";
  const status = response?.status ?? "unknown";
  const responseMessage = typeof response?.data?.message === "string" ? response.data.message : null;
  const responseKeys = response?.data && typeof response.data === "object" ? Object.keys(response.data) : [];
  const value = responseMessage
    ? `${status} ${method} ${url}: ${responseMessage}`
    : `${status} ${method} ${url}`;

  return {
    ...event,
    tags: {
      ...event.tags,
      error_source: "axios_response_rejection",
      http_status: String(status),
      http_method: method,
    },
    extra: {
      ...event.extra,
      request_url: url,
      axios: {
        status,
        method,
        url,
        responseMessage,
        responseKeys,
      },
    },
    fingerprint: ["axios-response-rejection", String(status), method, url],
    exception: {
      values: [
        {
          type: "AxiosResponseError",
          value,
        },
      ],
    },
  };
};

export const filterSentryEvent = (event, hint) => {
  const response = getAxiosResponseRejection(hint?.originalException);

  if (!response) return event;

  const axiosLikeError = {
    response,
    config: response.config,
  };

  if (!shouldCaptureAxiosError(axiosLikeError)) return null;

  return normalizeAxiosResponseRejectionEvent(event, response);
};

const getBundledSentryApplicationKey = () => {
  const applicationKey = globalThis.__SENTRY_APPLICATION_KEY__;

  return applicationKey === SENTRY_APPLICATION_KEY ? applicationKey : null;
};

export const buildSentryIntegrations = (router, sentry = Sentry) => {
  const integrations = [
    sentry.browserTracingIntegration({
      router,
      routeLabel: "path",
    }),
    sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ];

  const applicationKey = getBundledSentryApplicationKey();

  if (applicationKey && typeof sentry.thirdPartyErrorFilterIntegration === "function") {
    integrations.push(
      sentry.thirdPartyErrorFilterIntegration({
        filterKeys: [applicationKey],
        behaviour: "drop-error-if-exclusively-contains-third-party-frames",
      }),
    );
  }

  return integrations;
};

/*
 * Setup Sentry for error tracking
 *
 * @param {string} dsn
 * @param {import("vue").App} app
 * @param {import("vue-router").Router} router
 */
export default function setupSentry(dsn, app, router) {
  if (!dsn || !app || !router) return;

  Sentry.init({
    app,
    dsn: dsn,
    integrations: buildSentryIntegrations(router),
    tracesSampleRate: 0.01,
    tracePropagationTargets: ["businessdraft.com/api"],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.2,
    beforeSend: filterSentryEvent,
    ignoreErrors: [
      "Non-Error promise rejection captured",
      "UnhandledRejection: Non-Error promise rejection captured with value: undefined",
    ],
  });
};
